import {
  DEFAULT_ACCENT_COLOR,
  DEFAULT_PRIMARY_COLOR,
  FONTS,
  HEX_COLOR_RE,
} from './constants';
import type { Branding, FontName } from './types';

export interface NormalizedBranding {
  logo_key: string | null;
  cover_key: string | null;
  primary_color: string;
  accent_color: string;
  font: FontName;
}

const FONT_IDS = FONTS.map((font) => font.id) as readonly string[];

export function sanitizeColor(value: unknown, fallback: string): string {
  if (typeof value === 'string' && HEX_COLOR_RE.test(value.trim())) return value.trim().toLowerCase();
  return fallback;
}

export function isFontName(value: unknown): value is FontName {
  return typeof value === 'string' && FONT_IDS.includes(value);
}

export function normalizeBranding(branding?: Branding | null): NormalizedBranding {
  return {
    logo_key: branding?.logo_key ?? null,
    cover_key: branding?.cover_key ?? null,
    primary_color: sanitizeColor(branding?.primary_color, DEFAULT_PRIMARY_COLOR),
    accent_color: sanitizeColor(branding?.accent_color, DEFAULT_ACCENT_COLOR),
    font: isFontName(branding?.font) ? branding.font : 'inter',
  };
}

export function fontStack(font: FontName | string | undefined): string {
  return FONTS.find((entry) => entry.id === font)?.stack ?? FONTS[0].stack;
}

/** CSS custom properties for the storefront theme. */
export function brandingCssVars(branding?: Branding | null): Record<string, string> {
  const normalized = normalizeBranding(branding);
  return {
    '--brand-primary': normalized.primary_color,
    '--brand-accent': normalized.accent_color,
    '--brand-font': fontStack(normalized.font),
  };
}

/** Public URL for an R2 object key. Returns null when there is no key or CDN. */
export function mediaUrl(key: string | null | undefined, cdnBaseUrl: string): string | null {
  if (!key) return null;
  const base = cdnBaseUrl.replace(/\/+$/, '');
  return `${base}/${key.replace(/^\/+/, '')}`;
}

/**
 * Relative contrast color for text on a brand color. Uses WCAG relative
 * luminance; returns '#ffffff' or '#111827'.
 */
export function readableTextColor(hex: string): '#ffffff' | '#111827' {
  const color = sanitizeColor(hex, DEFAULT_PRIMARY_COLOR).replace('#', '');
  const full = color.length === 3 ? color.split('').map((c) => c + c).join('') : color;
  const r = Number.parseInt(full.slice(0, 2), 16) / 255;
  const g = Number.parseInt(full.slice(2, 4), 16) / 255;
  const b = Number.parseInt(full.slice(4, 6), 16) / 255;
  const channel = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? '#111827' : '#ffffff';
}
